import { useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { saveBlob } from "@/shared/download";
import { attachmentDownloadUrl, fetchAttachmentBlob } from "../api";
import { ImageLightbox } from "./ImageLightbox";

interface AttachmentItem {
  id: number;
  file_name: string;
  content_type: string;
}

interface AttachmentListProps {
  attachments: AttachmentItem[];
}

/** Determina se l'allegato e' un'immagine renderizzabile inline. */
function isImage(contentType: string): boolean {
  return (contentType || "").toLowerCase().startsWith("image/");
}

/**
 * Lista allegati di un messaggio marketplace ricevuto.
 *
 * Audit 2026-05-28 (Davide): "migliorare la gestione foto, sia in
 * ricezione che invio: preview + scarica in Downloads". Versione
 * precedente listava SOLO link testuali sottolineati — utile per i
 * documenti ma rendeva invisibile la natura visuale di un reclamo
 * cliente ("foto del difetto").
 *
 * Comportamento:
 * - ``image/*``: render thumbnail 120px cliccabile → apre ImageLightbox
 *   fullscreen. Bottone "Scarica" sempre disponibile sotto al thumb.
 * - Altri MIME (PDF, ZIP, XLSX...): chip con icona file generica +
 *   nome file + bottone "Scarica".
 *
 * Bug 2026-06-15 (Davide): "scaricare allegati non funziona sempre". Il
 * download era un ``<a href download>`` puro: se il backend rispondeva con
 * errore (404 dopo che un sync ha rigenerato gli id, URL firmato scaduto)
 * l'utente vedeva "non succede niente". Ora il download passa da
 * ``fetchAttachmentBlob`` → in caso di errore mostriamo un messaggio
 * leggibile (role="alert"); in caso di successo salviamo il blob via anchor
 * (Electron lo intercetta → ~/Downloads).
 */
export function AttachmentList({ attachments }: AttachmentListProps) {
  // Indice (nella lista delle SOLE immagini) della foto aperta nel lightbox.
  // null = lightbox chiuso. Usiamo l'indice e non l'oggetto perche' la galleria
  // naviga prev/next su tutte le immagini dello stesso messaggio.
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (attachments.length === 0) return null;

  // Solo le immagini finiscono nella galleria (i PDF/ZIP restano chip-scarica).
  const imageAttachments = attachments.filter((a) => isImage(a.content_type));
  const galleryImages = imageAttachments.map((a) => ({
    src: attachmentDownloadUrl(a.id),
    fileName: a.file_name,
  }));

  async function handleDownload(attachment: AttachmentItem): Promise<void> {
    setDownloadingId(attachment.id);
    setError(null);
    try {
      const blob = await fetchAttachmentBlob(attachment.id);
      saveBlob(blob, attachment.file_name);
    } catch (err) {
      setError(
        `Impossibile scaricare "${attachment.file_name}": ${prettifyApiError(err)} ` +
          "Ricarica il thread e riprova.",
      );
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="mt-2 border-t border-line/70 pt-2">
      <div className="flex flex-wrap gap-2">
        {attachments.map((attachment) => {
          const url = attachmentDownloadUrl(attachment.id);
          if (isImage(attachment.content_type)) {
            return (
              <figure
                key={attachment.id}
                className="flex flex-col gap-1 rounded-md border border-line bg-card p-1.5"
              >
                <button
                  type="button"
                  onClick={() =>
                    setPreviewIndex(imageAttachments.findIndex((a) => a.id === attachment.id))
                  }
                  className="block overflow-hidden rounded-sm border border-line bg-paper-2 hover:border-accent"
                  title={`Apri anteprima ${attachment.file_name}`}
                  aria-label={`Apri anteprima ${attachment.file_name}`}
                >
                  <Thumbnail src={url} fileName={attachment.file_name} />
                </button>
                <figcaption className="flex items-center justify-between gap-1">
                  <span
                    className="truncate text-[10.5px] text-ink-3 font-mono max-w-[100px]"
                    title={attachment.file_name}
                  >
                    {attachment.file_name}
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleDownload(attachment)}
                    disabled={downloadingId === attachment.id}
                    className="text-[10.5px] text-accent hover:underline shrink-0 disabled:opacity-60"
                    title="Scarica nella cartella Download"
                  >
                    {downloadingId === attachment.id ? "Scarico…" : "Scarica"}
                  </button>
                </figcaption>
              </figure>
            );
          }
          return (
            <span
              key={attachment.id}
              className="inline-flex items-center gap-2 rounded-md border border-line bg-card px-2.5 py-1 text-[11.5px] text-ink-2"
            >
              <FileIcon />
              <span className="font-mono truncate max-w-[200px]" title={attachment.file_name}>
                {attachment.file_name}
              </span>
              <button
                type="button"
                onClick={() => void handleDownload(attachment)}
                disabled={downloadingId === attachment.id}
                className="text-accent hover:underline shrink-0 disabled:opacity-60"
                title="Scarica nella cartella Download"
              >
                {downloadingId === attachment.id ? "Scarico…" : "Scarica"}
              </button>
            </span>
          );
        })}
      </div>

      {error && (
        <p role="alert" className="mt-2 text-[11.5px] text-red">
          {error}
        </p>
      )}

      {previewIndex !== null && galleryImages.length > 0 && (
        <ImageLightbox
          images={galleryImages}
          initialIndex={previewIndex}
          onClose={() => setPreviewIndex(null)}
        />
      )}
    </div>
  );
}

/**
 * Thumbnail 120px di un allegato immagine con fallback su errore di
 * caricamento. Audit (LOW): senza ``onError`` un 404 (id allegato
 * rigenerato dal sync, URL firmato scaduto) lasciava l'icona "immagine
 * rotta" del browser senza spiegazione — incoerente con ProductRow del
 * ContextPanel, che gia' degrada a un placeholder testuale. Qui replichiamo
 * lo stesso pattern: stato ``errored`` per-immagine → placeholder
 * "anteprima non disponibile".
 */
function Thumbnail({ src, fileName }: { src: string; fileName: string }) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <div className="grid h-[120px] w-[120px] place-items-center bg-paper-2 px-2 text-center text-[10px] font-medium text-ink-3">
        Anteprima non disponibile
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={fileName}
      loading="lazy"
      decoding="async"
      onError={() => setErrored(true)}
      className="block h-[120px] w-[120px] object-cover"
    />
  );
}

function FileIcon() {
  return (
    <svg
      className="h-3.5 w-3.5 fill-none stroke-current stroke-[1.6] text-ink-3"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M14 3 H7 a2 2 0 0 0 -2 2 v14 a2 2 0 0 0 2 2 h10 a2 2 0 0 0 2 -2 V8 Z" />
      <path d="M14 3 V8 H19" />
    </svg>
  );
}
